import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { H2 } from "../../../../components/text/headings";
import React from "react";
import { ResponsiveBar } from "@nivo/bar";
import { CaretDown } from "@phosphor-icons/react";
import LocalSpinner from "../../../../components/LocalSpinner";
import { getFormattedNumValue } from "../../../../utils";
import { shortenText } from "utils/text-utils";

const SupplierComparison = ({ year, yearByYearEmissionsData, isLoading }) => {
  const [averageType, setAverageType] = React.useState("Total Emissions");

  const averageBase = getAverageTotalEmission(yearByYearEmissionsData, year, averageType);
  const constructedAverageChartData = constructAverageChartData(
    yearByYearEmissionsData,
    year,
    averageBase,
    averageType
  );
  const constructedTargetTrackingChartData = constructTargetTrackingChartData(yearByYearEmissionsData, year);

  if (isLoading) {
    return <LocalSpinner />;
  }

  return (
    <Box>

      <Accordion allowToggle>
        <AccordionItem>

          <AccordionButton>
            <H2 fontSize="body.2xl">Compare</H2>
            <AccordionIcon mr={0} ml="auto" />
          </AccordionButton>

          <AccordionPanel mt={4}>
            <Tabs variant="primary" isLazy={true}>
              <TabList>
                <Tab>Average</Tab>
                <Tab>Target Tracking</Tab>
              </TabList>

              <Divider />

              <TabPanels>
                <TabPanel>
                  <AverageDropdown setSelected={setAverageType} selected={averageType} />
                  <DivergingBarChart data={constructedAverageChartData} base={averageBase} />
                </TabPanel>

                <TabPanel>
                  <DivergingBarChart data={constructedTargetTrackingChartData} base={0} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </AccordionPanel>

        </AccordionItem>
      </Accordion>


    </Box>
  );
};

/**
 * Constructs chart data for supplier comparison based on emissions or intensity values.
 * Filters out suppliers with zero/undefined emissions and formats data for bar chart visualization.
 *
 * @param {Array} yearByYearEmissionsData - Array of supplier objects with yearly emission data
 * @param {string|number} year - Target year to extract data from
 * @param {number} cutoff - Average baseline value (currently unused but kept for future implementation)
 * @param {string} averageType - Either "Intensity" or "Total Emissions" to determine data source
 * @returns {Array} Formatted data array with supplier name and value for chart rendering
 */
const constructAverageChartData = (yearByYearEmissionsData, year, cutoff, averageType) => {
  const data = yearByYearEmissionsData
    // Filter out suppliers with no emission data for the selected year
    .filter((supplier) => {
      const value =
        averageType === "Intensity"
          ? supplier?.yearlyEmissionData?.[year]?.intensity
          : supplier?.yearlyEmissionData?.[year]?.totalCo2Emissions;

      return value !== 0 && value !== undefined;
    })
    .map((supplier) => {
      // Extract value based on selected metric type
      const dataInNumber =
        averageType === "Intensity"
          ? supplier?.yearlyEmissionData?.[year]?.intensity || 0
          : supplier?.yearlyEmissionData?.[year]?.totalCo2Emissions || 0;

      return {
        supplier: supplier?.supplierName,
        value: dataInNumber,
      };
    });

  return data;
};

/**
 * Constructs chart data for target tracking visualization showing supplier performance vs targets.
 * Calculates percentage deviation from target emissions and caps extreme values for chart readability.
 *
 * targets come from useGetSuppliersYearlyData??
 *
 * @param {Array} yearByYearEmissionsData - Array of supplier objects with yearly emission data
 * @param {string|number} year - Target year to extract data from
 * @returns {[{supplier: string, value: number}]} Formatted data array with supplier name and value = percentage (where 100% = 100, not 1) deviation from target
 */
const constructTargetTrackingChartData = (yearByYearEmissionsData, year) => {
  let data = yearByYearEmissionsData
    // Filter out suppliers with no emission data for the selected year
    .filter(
      (supplier) =>
        supplier?.yearlyEmissionData?.[year]?.totalCo2Emissions !== 0 &&
        supplier?.yearlyEmissionData?.[year]?.totalCo2Emissions !== undefined
    )
    .map((supplier) => {
      // Calculate percentage deviation from target: ((actual - target) / target) * 100
      let dataInNumber = supplier?.yearlyEmissionData?.[year]?.target
        ? ((supplier?.yearlyEmissionData?.[year]?.totalCo2Emissions - supplier?.yearlyEmissionData?.[year]?.target) /
            supplier?.yearlyEmissionData?.[year]?.target) *
          100
        : 0;

      // Cap extreme values at 1000% for better chart visualization
      if (dataInNumber > 1000) {
        dataInNumber = 1000;
      }

      return {
        supplier: supplier?.supplierName,
        value: dataInNumber, // Positive = above target, Negative = below target
      };
    });
  return data;
};

const getAverageTotalEmission = (data, year, averageType) => {
  if (!Array.isArray(data) || data.length === 0) return 0;

  // Extract totalCo2Emissions for the given year
  const dataInNumber = data.map((supplier) =>
    averageType === "Intensity"
      ? supplier?.yearlyEmissionData?.[year]?.intensity || 0
      : supplier?.yearlyEmissionData?.[year]?.totalCo2Emissions || 0
  );

  // Calculate average
  const total = dataInNumber.reduce((sum, val) => sum + val, 0);
  return total / dataInNumber.length;
};

/**
 * Renders a diverging bar chart using Nivo's ResponsiveBar component.
 * Bars extend left or right from a base value, allowing comparison of values above and below the base.
 * @param base
 * @param data
 * @param max
 * @returns {Element}
 * @constructor
 */
const DivergingBarChart = ({ base = 0, data = [], max }) => {
  // Transform data so bars start from base value
  const transformedData = data
    .map((d) => ({
      ...d,
      diff: d?.value - base,
      originalValue: d?.value,
    }))
    .sort((a, b) => a.diff - b.diff);

  // Find max abs value to make chart symmetrical around base
  const maxAbs = Math.max(...transformedData.map((d) => Math.abs(d.diff)));

  const barHeight = 30;
  const minHeight = 500;
  const chartHeight = Math.max(minHeight, data.length * barHeight);

  return (
    <Box height={`${chartHeight}px`} width="100%">
      <ResponsiveBar
        data={transformedData}
        keys={["diff"]}
        indexBy="supplier"
        layout="horizontal"
        margin={{ top: 20, right: 30, bottom: 40, left: 150 }}
        padding={0.3}
        valueScale={{ type: "linear", min: -maxAbs, max: maxAbs }}
        indexScale={{ type: "band", round: true }}
        colors={(bar) => (bar.data.originalValue >= base ? "#ff4444" : "#66bb00")}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          format: (v) => (v + base).toLocaleString(),
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          format: (supplierName) => shortenText(supplierName, 20, 15),
        }}
        enableGridX
        enableGridY={false}
        label={(bar) => (typeof bar.data.originalValue === "number" ? bar.data.originalValue.toLocaleString() : "")}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor="#fff"
        theme={{
          axis: {
            ticks: {
              text: { fill: "#374151" },
            },
          },
          grid: {
            line: { stroke: "#ccc", strokeWidth: 1 },
          },
        }}
        markers={[
          {
            axis: "x",
            value: 0,
            lineStyle: { stroke: "#000", strokeWidth: 2 },
          },
        ]}
        tooltip={({ data }) => (
          <Box bg="white" p="8px" border="1px solid #ccc" borderRadius="4px">
            <Text fontWeight="bold" color={data?.color}>
              {data.supplier}
            </Text>
            <Text>Value: {getFormattedNumValue(Number(data.originalValue), 2)}</Text>
          </Box>
        )}
      />
    </Box>
  );
};

const AverageDropdown = ({ selected, setSelected }) => {
  const options = ["Total Emissions", "Intensity"];

  return (
    <Menu>
      <MenuButton as={Button} variant="outline" rightIcon={<CaretDown size={16} />}>
        {selected}
      </MenuButton>
      <MenuList>
        {options.map((option) => (
          <MenuItem
            key={option}
            onClick={() => setSelected(option)}
            fontWeight={selected === option ? "bold" : "normal"}
          >
            {option}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

export default SupplierComparison;
